import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useClinic } from '@/contexts/ClinicContext';
import { motion, AnimatePresence } from 'framer-motion';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  const { isAdmin, isClinicStaff, isLoading: isClinicLoading } = useClinic();
  const location = useLocation();

  const showLoading = isLoading || isClinicLoading;
  const isOrgRoute = location.pathname.startsWith('/org/');

  // Redirect to login if not authenticated (after loading is done)
  if (!showLoading && !isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Anyone who is not admin OR clinic_staff falls through to the practitioner portal.
  if (!showLoading && !isOrgRoute && !isAdmin && !isClinicStaff && isAuthenticated) {
    return <Navigate to="/practitioner-portal/patients" replace />;
  }

  return (
    <>
      <AnimatePresence>
        {showLoading && (
          <motion.div
            initial={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
            className="fixed inset-0 z-[9999] min-h-screen bg-white flex items-center justify-center"
          >
            <div className="text-center">
              <img
                src="/nimbus_logo.svg"
                alt="Nimbus"
                className="h-8 mx-auto mb-6"
              />
              <div className="w-9 h-9 rounded-full border-[3px] border-slate-200 border-t-brand-teal animate-spin mx-auto"></div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      {!showLoading && isAuthenticated && (isOrgRoute || isAdmin || isClinicStaff) && children}
    </>
  );
};

export default ProtectedRoute;
